import { Link } from "@remix-run/react";
import { useTranslation } from "react-i18next";

import isotipoImg from "~/assets/academia-isotipo.png";
import { ThemeSwitch } from "~/components/ThemeToggle/ThemeSwitch";

type FooterLink = { label: string; href: string };
type FooterColumn = { title: string; links: FooterLink[] };

export function LandingNewFooter() {
  const { t } = useTranslation();
  // 2026-07-31: una sola columna. Antes eran tres (Explorar / AcademIA / Legal) y las
  // dos primeras apuntaban a superficies que el lanzamiento ocultó (#rutas ya no existe
  // en la home, y Cursos y Quiénes somos salieron del nav).
  const columns = t("landing.footerNew.columns", {
    returnObjects: true,
  }) as Record<"useful", FooterColumn>;

  return (
    <footer className="relative z-10 bg-landing-ink-950 text-landing-on-dark">
      <div className="mx-auto max-w-[1280px] px-6 py-16 sm:px-8">
        {/* ═══ Top: brand + columns ═══ */}
        <div className="grid gap-12 md:grid-cols-[1fr_auto]">
          {/* Brand block */}
          <div>
            <Link
              to="/"
              data-cuelume-hover="tick"
              data-cuelume-press
              className="inline-flex items-center gap-2.5"
            >
              <img
                src={isotipoImg}
                alt=""
                width={44}
                height={44}
                className="h-10 w-10 object-contain"
              />
              <span className="font-display text-[26px] font-semibold leading-none tracking-tight">
                <span className="text-landing-on-dark">Academ</span>
                <span className="text-landing-red">IA</span>
              </span>
            </Link>
            <p className="mt-4 max-w-[280px] text-[14px] leading-relaxed text-[rgba(255,255,255,0.60)]">
              {t("landing.footerNew.tagline")}
            </p>
            {/* Redes — TODO: confirmar handles reales de AcademIA */}
            {/* El `gap` es chico a propósito: cada link es una caja de 32px con el ícono de
                18px centrado, así que el aire entre glifos lo pone el padding, no el gap.
                Sumados dan los mismos 16px de separación visual que había cuando el link
                medía exactamente lo que el ícono. */}
            <div className="mt-5 flex items-center gap-0.5">
              {[
                {
                  label: "Instagram",
                  href: "https://instagram.com/cursosacademia",
                  path: "M12 2.16c3.2 0 3.58.01 4.85.07 3.25.15 4.77 1.69 4.92 4.92.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.15 3.23-1.66 4.77-4.92 4.92-1.27.06-1.64.07-4.85.07s-3.58-.01-4.85-.07c-3.26-.15-4.77-1.7-4.92-4.92-.06-1.27-.07-1.64-.07-4.85s.01-3.58.07-4.85C2.38 3.92 3.9 2.38 7.15 2.23 8.42 2.17 8.8 2.16 12 2.16zM12 0C8.74 0 8.33.01 7.05.07 2.7.27.27 2.69.07 7.05.01 8.33 0 8.74 0 12s.01 3.67.07 4.95c.2 4.36 2.62 6.78 6.98 6.98 1.28.06 1.69.07 4.95.07s3.67-.01 4.95-.07c4.35-.2 6.78-2.62 6.98-6.98.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95C23.73 2.7 21.31.27 16.95.07 15.67.01 15.26 0 12 0zm0 5.84A6.16 6.16 0 1 0 12 18.16 6.16 6.16 0 0 0 12 5.84zm0 10.15A4 4 0 1 1 12 8a4 4 0 0 1 0 8zm6.41-11.85a1.44 1.44 0 1 0 0 2.88 1.44 1.44 0 0 0 0-2.88z",
                },
                {
                  label: "TikTok",
                  href: "https://tiktok.com/@cursosacademia",
                  path: "M12.53.02C13.84 0 15.14.01 16.44 0c.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z",
                },
                {
                  label: "LinkedIn",
                  href: "https://linkedin.com/company/cursosacademia",
                  path: "M20.45 20.45h-3.55v-5.57c0-1.33-.03-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.36V9h3.41v1.56h.05c.47-.9 1.63-1.85 3.36-1.85 3.6 0 4.27 2.37 4.27 5.45v6.29zM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12zM7.12 20.45H3.56V9h3.56v11.45z",
                },
                {
                  label: "X",
                  href: "https://x.com/cursosacademia",
                  path: "M18.9 1.15h3.68l-8.04 9.19L24 22.85h-7.41l-5.8-7.58-6.64 7.58H.47l8.6-9.83L0 1.15h7.59l5.24 6.93 6.07-6.93zm-1.29 19.5h2.04L6.49 3.24H4.3l13.31 17.41z",
                },
                {
                  label: "YouTube",
                  href: "https://youtube.com/@cursosacademia",
                  path: "M23.5 6.19a3.02 3.02 0 0 0-2.12-2.14C19.5 3.55 12 3.55 12 3.55s-7.5 0-9.38.5A3.02 3.02 0 0 0 .5 6.19C0 8.07 0 12 0 12s0 3.93.5 5.81a3.02 3.02 0 0 0 2.12 2.14c1.88.5 9.38.5 9.38.5s7.5 0 9.38-.5a3.02 3.02 0 0 0 2.12-2.14C24 15.93 24 12 24 12s0-3.93-.5-5.81zM9.55 15.57V8.43L15.82 12l-6.27 3.57z",
                },
              ].map((net) => (
                <a
                  key={net.label}
                  href={net.href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`AcademIA en ${net.label}`}
                  data-cuelume-hover="tick"
                  data-cuelume-press
                  // El área de toque era de 18x18: el tamaño del ícono, sin padding. WCAG 2.2
                  // (2.5.8) pide 24x24 como mínimo y en mobile eso se falla tocando la red
                  // equivocada. La caja pasa a 32x32; el ícono sigue dibujándose a 18.
                  className="inline-flex size-8 items-center justify-center text-[rgba(255,255,255,0.50)] transition-colors hover:text-[#E2604F]"
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d={net.path} />
                  </svg>
                </a>
              ))}
            </div>
          </div>

          {/* Columns */}
          {(["useful"] as const).map((key) => {
            const col = columns[key];
            return (
              <div key={key}>
                {/* #E2604F, no `landing-red`: el rojo de marca sobre el negro del footer
                    da 3.5:1 a 11px, abajo de AA. Es el mismo tono del hover de las redes. */}
                <p className="font-landing mb-4 text-[11px] font-semibold uppercase tracking-[0.12em] text-[#E2604F]">
                  {col.title}
                </p>
                <ul className="flex flex-col gap-2.5">
                  {col.links.map((link) => (
                    <li key={link.href}>
                      <Link
                        to={link.href}
                        data-cuelume-hover="tick"
                        data-cuelume-press
                        className="text-[14px] text-[rgba(255,255,255,0.70)] transition-colors hover:text-landing-on-dark"
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* ═══ Bottom: divider + copyright ═══ */}
        <div className="mt-12 flex flex-col gap-4 border-t border-[rgba(255,255,255,0.10)] pt-6 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-[12px] text-[rgba(255,255,255,0.60)]">
            {t("landing.footerNew.copyright")}
          </p>
          <div className="flex flex-wrap items-center gap-3 sm:justify-end">
            <ThemeSwitch tone="on-dark" showStatus />
            <p className="font-landing text-[11px] tracking-[0.1em] text-[rgba(255,255,255,0.55)]">
              cursosacademia.ar
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
